import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { resolveCalendarMaps } from '@/theme/maps/calendar';
import type { Reservation } from '@/api/reservations';
import {
  adjustPendingCharge,
  chargePendingChargeNow,
  waiveReservationPendingCharge,
} from '@/api/pendingCharges';
import { serverMessage } from '@/api/serverMessage';
import { formatMinorMoney, minorUnitFactor } from '@/utils/v2Format';
import { declineReasonLine, type DeclineReason } from '@/utils/declineReason';
import { settledFeeLabel } from '@/utils/pendingChargeLabel';
import { scrubBrand } from '@/utils/whitelabel';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_TIME_YEAR, formatInstant } from '@/utils/instantFormat';

type PendingAction = 'adjust' | 'charge' | 'waive' | null;

function decimalsFor(currency: string): number {
  const factor = minorUnitFactor(currency);
  return factor === 1 ? 0 : factor === 1000 ? 3 : 2;
}

function minorToMajorInput(amountMinor: number, currency: string): string {
  return (amountMinor / minorUnitFactor(currency)).toFixed(decimalsFor(currency));
}

function majorToMinor(value: string, currency: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const major = Number(trimmed);
  if (!Number.isFinite(major)) return null;
  return Math.round(major * minorUnitFactor(currency));
}

function amountLabel(amountMinor: number, currency: string): string {
  return currency === '' ? '—' : formatMinorMoney(amountMinor, currency);
}

/** When the charge runs, on the venue's clock (DNKADM-0476 F-05). */
function scheduledLabel(value: string | null, timeZone: string | null): string {
  return formatInstant(value, timeZone, DATE_TIME_YEAR);
}

function statusLabel(value: string): string {
  if (value === '') return 'Unknown';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function actionError(error: unknown): string {
  return serverMessage(error) ?? 'The pending charge could not be updated. Please try again.';
}

/** Humanize a failure code, e.g. `card_declined` -> "Card declined". */
function humanizeCode(code: string): string {
  const spaced = code.replace(/[_-]+/g, ' ').trim();
  if (spaced.length === 0) return code;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function chargeFailureMessage(
  failureCode: string | null,
  declineReason: DeclineReason | null | undefined,
): string {
  // DNKAPI-0676: a known decline reason says why; OTHER/unknown keep the code line.
  const reasonLine = declineReasonLine(declineReason);
  if (reasonLine !== null) return `Charge failed. ${reasonLine}`;
  if (failureCode === null) return 'Charge failed.';
  return `Charge failed (${scrubBrand(humanizeCode(failureCode))}).`;
}

export function ReservationPendingChargeBlock({
  reservation,
  canWrite = true,
  canEditFee = true,
  onChanged,
}: {
  reservation: Reservation;
  /**
   * Whether this host lets the operator move the money. `false` for a view-only host
   * (My schedule): Adjust / Charge now / Waive are ABSENT, so their dialogs can never
   * open and no charge write is reachable. The amount, schedule and status still
   * render — they come from the reservation read itself. Defaults to `true`.
   */
  canWrite?: boolean;
  /**
   * Whether this session may Adjust or Waive the fee (DNKADM-0375). dink-api keeps both
   * OWNER/ADMIN while opening Charge now to STAFF, so a STAFF session gets Charge now
   * alone. Defaults to `true`.
   */
  canEditFee?: boolean;
  onChanged: (reservationUuid: string) => Promise<void>;
}) {
  const cm = resolveCalendarMaps(useTheme());
  const calendarZone = useCalendarZone();
  const charge = reservation.pendingCharge;
  const [action, setAction] = useState<PendingAction>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (charge === null) return null;

  const storedReasonLine = declineReasonLine(charge.declineReason);

  const currencyAvailable = charge.currency.trim().length > 0;
  const amountMinor = currencyAvailable ? majorToMinor(amount, charge.currency) : null;
  const amountInvalid = !currencyAvailable || amountMinor === null || amountMinor <= 0;
  const actionable =
    canWrite && charge.status === 'pending' && reservation.reservationUuid !== null;

  const openAction = (next: Exclude<PendingAction, null>) => {
    setError(null);
    if (next === 'adjust') {
      setAmount(currencyAvailable ? minorToMajorInput(charge.amountMinor, charge.currency) : '');
    }
    setAction(next);
  };

  const refreshAfterMutation = async () => {
    if (reservation.reservationUuid === null) return;
    try {
      await onChanged(reservation.reservationUuid);
    } catch {
      setError('The charge changed, but the reservation could not be refreshed.');
    }
  };

  const submitAdjust = async () => {
    if (!currencyAvailable || amountInvalid || amountMinor === null) return;
    setBusy(true);
    setError(null);
    try {
      await adjustPendingCharge(charge.id, { amountMinor });
    } catch (err) {
      setError(actionError(err));
      setBusy(false);
      return;
    }
    setAction(null);
    setBusy(false);
    await refreshAfterMutation();
  };

  const submitChargeNow = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await chargePendingChargeNow(charge.id);
      if (updated?.status === 'failed') {
        setError(chargeFailureMessage(updated.failureCode, updated.declineReason));
        if (reservation.reservationUuid !== null) {
          try {
            await onChanged(reservation.reservationUuid);
          } catch {
            // Retain the actionable failure message; the operator must not mistake
            // the declined charge for a success even when the refresh also fails.
          }
        }
        setBusy(false);
        return;
      }
    } catch (err) {
      setError(actionError(err));
      setBusy(false);
      return;
    }
    setAction(null);
    setBusy(false);
    await refreshAfterMutation();
  };

  const submitWaive = async () => {
    if (reservation.reservationUuid === null) return;
    setBusy(true);
    setError(null);
    try {
      await waiveReservationPendingCharge(reservation.reservationUuid);
    } catch (err) {
      setError(actionError(err));
      setBusy(false);
      return;
    }
    setAction(null);
    setBusy(false);
    await refreshAfterMutation();
  };

  const closeAction = () => {
    if (busy) return;
    setAction(null);
    setError(null);
  };

  const currencyAdornment =
    charge.currency === '' ? undefined : (
      <InputAdornment position="start">{charge.currency}</InputAdornment>
    );

  return (
    <Box sx={{ mt: 1.25, mb: 1, p: 1.5, border: `1px solid ${cm.gutterBorder}`, borderRadius: 1 }}>
      {/* DNKADM-0489 follow-up: a settled fee is no longer "pending" (same mapper as the tile). */}
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        {settledFeeLabel(charge.status) ?? 'Pending charge'}
      </Typography>
      <Stack sx={{ mt: 0.75, gap: 0.35 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          {amountLabel(charge.amountMinor, charge.currency)}
        </Typography>
        <Typography sx={{ fontSize: 12, color: cm.textMeta }}>
          Runs at {scheduledLabel(charge.scheduledAt, calendarZone)}
        </Typography>
        <Chip size="small" label={statusLabel(charge.status)} sx={{ alignSelf: 'flex-start', mt: 0.35 }} />
        {charge.status === 'failed' && storedReasonLine !== null && error === null && (
          // DNKAPI-0676: why the stored charge was declined (hidden while the charge-now
          // error already says it).
          <Typography
            data-testid="pending-charge-decline-reason"
            sx={{ fontSize: 12, color: cm.textMeta }}
          >
            {storedReasonLine}
          </Typography>
        )}
      </Stack>

      {actionable && (
        <Stack direction="row" sx={{ mt: 1.25, gap: 0.5, flexWrap: 'wrap' }}>
          {canEditFee && <Button size="small" onClick={() => openAction('adjust')}>Adjust</Button>}
          <Button size="small" variant="contained" onClick={() => openAction('charge')}>Charge now</Button>
          {canEditFee && (
            <Button size="small" color="inherit" onClick={() => openAction('waive')}>Waive</Button>
          )}
        </Stack>
      )}

      {error !== null && action === null && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      <Dialog open={action === 'adjust'} onClose={closeAction} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust pending charge</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the new amount. This changes the charge that will run at the scheduled time.
          </DialogContentText>
          <TextField
            fullWidth
            label="Amount"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            disabled={busy || !currencyAvailable}
            error={amount.trim().length > 0 && amountInvalid}
            helperText={
              !currencyAvailable
                ? 'Currency is not available yet.'
                : amount.trim().length > 0 && amountInvalid
                  ? 'Enter an amount greater than 0.'
                  : ' '
            }
            slotProps={{ input: { startAdornment: currencyAdornment } }}
            sx={{ mt: 1.5 }}
          />
          {error !== null && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} color="inherit" disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitAdjust()} disabled={busy || amountInvalid}>
            Apply adjustment
          </Button>
        </DialogActions>
      </Dialog>

      <PendingConfirmDialog
        open={action === 'charge'}
        title="Charge this card now?"
        body="This runs the pending charge immediately."
        confirmLabel="Charge now"
        busy={busy}
        error={error}
        onCancel={closeAction}
        onConfirm={() => void submitChargeNow()}
      />
      <PendingConfirmDialog
        open={action === 'waive'}
        title="Waive this pending charge?"
        body="This cancels the pending charge so it will not run."
        confirmLabel="Waive charge"
        busy={busy}
        error={error}
        onCancel={closeAction}
        onConfirm={() => void submitWaive()}
      />
    </Box>
  );
}

function PendingConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
        {error !== null && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}
